import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Activity, Ban, KeyRound, Lock, MessageSquareWarning, Network, Pencil, Plus, Trash2 } from 'lucide-react'
import { departmentApi, governanceApi } from '../services/api'
import { dialog } from '../services/dialog'
import { PageShell } from '../components/layout/PageShell'
import { PageBrow } from '../components/layout/PageBrow'
import { EmptyState } from '../components/patterns'
import { RuleForm } from '../components/governance/RuleForm'
import { ActivityTable } from '../components/governance/ActivityTable'
import {
  ENFORCEMENT_MODE_OPTIONS,
  type GovernanceEnforcementMode,
  type GovernancePolicy,
  type GovernanceRule,
  type GovernanceRuleAction,
  type GovernanceRuleType,
} from '../components/governance/types'

// Strictness order, softest to strictest — MUST mirror
// apps/server/src/lib/governance/policyCache.ts MODE_ORDER. A department
// layer may only pick a mode at or above the server layer's index (the
// server itself re-validates this on PUT; disabling the softer options here
// is just a friendlier front line).
const MODE_ORDER: GovernanceEnforcementMode[] = ['notify_only', 'post_delivery', 'realtime_hold']

interface DeptListItem {
  id: string
  name: string
}

const SECTIONS: { type: GovernanceRuleType; title: string; description: string; icon: ReactNode }[] = [
  {
    type: 'topic',
    title: 'Forbidden topics',
    description: 'AI screens messages against a plain-language description of what is not allowed.',
    icon: <MessageSquareWarning size={14} className="text-muted-foreground" />,
  },
  {
    type: 'keyword',
    title: 'Trigger words',
    description: 'Deterministic, case-insensitive match against a list of phrases.',
    icon: <KeyRound size={14} className="text-muted-foreground" />,
  },
  {
    type: 'barrier',
    title: 'Barriers',
    description: 'Block all communication between two people or departments — always Block.',
    icon: <Ban size={14} className="text-muted-foreground" />,
  },
]

function actionChipClass(action: GovernanceRuleAction): string {
  return action === 'block' ? 'bg-destructive/10 text-destructive' : 'bg-amber-500/15 text-amber-500'
}

/** Server Admin -> Departments -> Policies (Task 9): the per-department
 *  add-on layer of Communication Governance. Reuses RuleForm + ActivityTable
 *  (Task 8) with departmentId set. The server-wide layer's rules are shown
 *  read-only ("locked") above the department's own rules — a department may
 *  only add STRICTER rules, never soften or remove the server layer. Access
 *  is the department head or a server admin; everyone else gets a 403 from
 *  the API, rendered here as a friendly card instead of a crash. */
export default function DepartmentPoliciesPage() {
  const { serverId, deptId } = useParams<{ serverId: string; deptId: string }>()
  const navigate = useNavigate()

  const [deptName, setDeptName] = useState<string | null>(null)
  const [policy, setPolicy] = useState<GovernancePolicy | null>(null)
  const [rules, setRules] = useState<GovernanceRule[]>([])
  const [serverPolicy, setServerPolicy] = useState<{ enforcementMode: GovernanceEnforcementMode; enabled: boolean } | null>(null)
  const [serverRules, setServerRules] = useState<GovernanceRule[]>([])
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savingPolicy, setSavingPolicy] = useState(false)

  // undefined = no panel open. Mirrors PoliciesTab: at most one panel open
  // page-wide so the page never shows two forms fighting for attention.
  const [creatingType, setCreatingType] = useState<GovernanceRuleType | null>(null)
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!serverId || !deptId) return
    setError(null)
    try {
      const res = await governanceApi.getDeptPolicy(serverId, deptId)
      setPolicy(res.data.policy)
      setRules(res.data.rules ?? [])
      setServerPolicy(res.data.serverPolicy ?? null)
      setServerRules(res.data.serverRules ?? [])
      setForbidden(false)
      setNotFound(false)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 403) { setForbidden(true); setLoading(false); return }
      if (status === 404) { setNotFound(true); setLoading(false); return }
      setError(err?.response?.data?.error || 'Failed to load department policy')
      setLoading(false)
      return
    }
    // Department name — best-effort and independent of the policy fetch;
    // departmentApi.list is open to any authenticated server member, so this
    // succeeds even in edge cases where governance access is narrower.
    try {
      const deptRes = await departmentApi.list(serverId)
      const list = (deptRes.data ?? []) as DeptListItem[]
      setDeptName(list.find((d) => d.id === deptId)?.name ?? null)
    } catch {
      // Non-fatal — the brow falls back to a generic title.
    }
    setLoading(false)
  }, [serverId, deptId])

  useEffect(() => { load() }, [load])

  // No policy row yet means the dept layer has never been saved — show what
  // a PUT would create (mirrors getOrCreatePolicy's defaults in
  // routes/governance.ts), the same fallback PoliciesTab uses server-side.
  const enabled = policy?.enabled ?? true
  const enforcementMode = policy?.enforcementMode ?? 'post_delivery'
  const serverMode = serverPolicy?.enabled ? serverPolicy.enforcementMode : null
  const floorIdx = serverMode ? MODE_ORDER.indexOf(serverMode) : 0

  const patchPolicy = async (data: { enabled?: boolean; enforcementMode?: GovernanceEnforcementMode }) => {
    if (!serverId || !deptId) return
    setSavingPolicy(true)
    setError(null)
    try {
      await governanceApi.putDeptPolicy(serverId, deptId, data)
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update policy')
    } finally {
      setSavingPolicy(false)
    }
  }

  const closeForms = () => { setCreatingType(null); setEditingRuleId(null) }
  const startCreate = (type: GovernanceRuleType) => { setEditingRuleId(null); setCreatingType(type) }
  const startEdit = (rule: GovernanceRule) => { setCreatingType(null); setEditingRuleId(rule.id) }
  const handleSaved = async () => { closeForms(); await load() }

  const toggleRuleEnabled = async (rule: GovernanceRule) => {
    if (!serverId) return
    setError(null)
    try {
      await governanceApi.updateRule(serverId, rule.id, { enabled: !rule.enabled })
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update rule')
    }
  }

  const handleDelete = async (rule: GovernanceRule) => {
    if (!serverId) return
    if (!(await dialog.confirm({ title: 'Delete rule', message: `Delete "${rule.name}"? This cannot be undone.`, tone: 'destructive', confirmLabel: 'Delete' }))) return
    setError(null)
    try {
      await governanceApi.deleteRule(serverId, rule.id)
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to delete rule')
    }
  }

  // Controls only render once we know the load succeeded — showing them
  // against still-default state before the real policy lands would flash
  // wrong values for a department that had actually disabled its layer.
  const showControls = !loading && !forbidden && !notFound

  const brow = (
    <PageBrow
      left={
        <div>
          <div className="text-base font-semibold text-foreground leading-tight truncate">{deptName ?? 'Department'} · Policies</div>
          <div className="text-xs text-muted-foreground">Communication governance for this department</div>
        </div>
      }
      onBack={() => navigate(-1)}
      right={
        showControls ? (
          <div className="flex items-center gap-3">
            <select
              value={enforcementMode}
              onChange={(e) => patchPolicy({ enforcementMode: e.target.value as GovernanceEnforcementMode })}
              disabled={savingPolicy || !enabled}
              className="dp-select"
              title="Enforcement mode"
            >
              {ENFORCEMENT_MODE_OPTIONS.map((o) => {
                const tooSoft = MODE_ORDER.indexOf(o.value) < floorIdx
                return (
                  <option key={o.value} value={o.value} disabled={tooSoft}>
                    {o.label}{tooSoft ? ' (softer than server floor)' : ''}
                  </option>
                )
              })}
            </select>
            <Toggle checked={enabled} onChange={() => patchPolicy({ enabled: !enabled })} disabled={savingPolicy} label={enabled ? 'Enabled' : 'Disabled'} />
          </div>
        ) : undefined
      }
    />
  )

  if (!serverId || !deptId) {
    return (
      <PageShell brow={brow}>
        <EmptyState icon={<Network size={28} />} title="Department not found" />
      </PageShell>
    )
  }

  if (forbidden) {
    return (
      <PageShell brow={brow}>
        <EmptyState icon={<Lock size={28} />} title="No access" description="Only the department head and server admins can manage these policies." />
      </PageShell>
    )
  }

  if (notFound) {
    return (
      <PageShell brow={brow}>
        <EmptyState icon={<Network size={28} />} title="Department not found" description="This department may have been deleted or moved." />
      </PageShell>
    )
  }

  return (
    <PageShell brow={brow}>
      <div className="p-6 space-y-4">
        {loading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <>
            {error && <div className="text-sm bg-destructive/10 text-destructive rounded px-3 py-2">{error}</div>}

            <div className="rounded-xl ring-1 ring-border bg-card/40 overflow-hidden">
              <div className="px-4 py-2.5 bg-muted/40 border-b border-border flex items-center gap-2.5">
                <Lock size={14} className="text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground">Server policy (locked)</div>
                  <div className="text-[11px] text-muted-foreground">Set at server level — departments can only add stricter rules.</div>
                </div>
                {serverMode && (
                  <span className="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    Floor: {serverMode.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
              {serverRules.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">No server-level rules.</div>
              ) : (
                <div className="divide-y divide-border">
                  {serverRules.map((r) => <LockedRuleRow key={r.id} rule={r} />)}
                </div>
              )}
            </div>

            {SECTIONS.map((s) => (
              <RuleSection
                key={s.type}
                serverId={serverId}
                deptId={deptId}
                section={s}
                rules={rules.filter((r) => r.type === s.type)}
                creating={creatingType === s.type}
                editingRuleId={editingRuleId}
                onStartCreate={() => startCreate(s.type)}
                onStartEdit={startEdit}
                onCancelForm={closeForms}
                onSaved={handleSaved}
                onToggleEnabled={toggleRuleEnabled}
                onDelete={handleDelete}
              />
            ))}

            <div className="rounded-xl ring-1 ring-border bg-card/40 overflow-hidden">
              <div className="px-4 py-2.5 bg-muted/40 border-b border-border flex items-center gap-2.5">
                <Activity size={14} className="text-muted-foreground" />
                <div className="text-sm font-semibold text-foreground">Activity</div>
                <span className="text-xs text-muted-foreground">Recent policy events for this department</span>
              </div>
              <div className="p-3">
                <ActivityTable serverId={serverId} departmentId={deptId} />
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`.dp-select{background:var(--color-muted);color:var(--color-foreground);border-radius:6px;padding:6px 10px;font-size:12px;outline:none;border:1px solid transparent;width:260px}.dp-select:focus{border-color:var(--color-primary)}.dp-select:disabled{opacity:0.6;cursor:not-allowed}`}</style>
    </PageShell>
  )
}

// Read-only mirror of PoliciesTab's RuleRow, minus the toggle / edit /
// delete controls — this is the server layer as seen from a department, and
// it can never be edited from here.
function LockedRuleRow({ rule }: { rule: GovernanceRule }) {
  const recipientCount = rule.recipientIds?.length ?? 0
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${rule.enabled ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{rule.name}</span>
          <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${actionChipClass(rule.action)}`}>
            {rule.action}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          {rule.surfaces.map((s) => (
            <span key={s} className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{s}</span>
          ))}
          {rule.type === 'barrier' && rule.barrierA && rule.barrierB && (
            <span className="text-[11px] text-muted-foreground font-mono">
              {rule.barrierA.kind}:{rule.barrierA.id.slice(0, 8)} ⇄ {rule.barrierB.kind}:{rule.barrierB.id.slice(0, 8)}
            </span>
          )}
          <span className="text-[11px] text-muted-foreground">
            {recipientCount > 0 ? `${recipientCount} recipient${recipientCount === 1 ? '' : 's'}` : 'Default recipients (dept head / server admins)'}
          </span>
        </div>
      </div>
    </div>
  )
}

function RuleSection({
  serverId, deptId, section, rules, creating, editingRuleId, onStartCreate, onStartEdit, onCancelForm, onSaved, onToggleEnabled, onDelete,
}: {
  serverId: string
  deptId: string
  section: { type: GovernanceRuleType; title: string; description: string; icon: ReactNode }
  rules: GovernanceRule[]
  creating: boolean
  editingRuleId: string | null
  onStartCreate: () => void
  onStartEdit: (rule: GovernanceRule) => void
  onCancelForm: () => void
  onSaved: () => void
  onToggleEnabled: (rule: GovernanceRule) => void
  onDelete: (rule: GovernanceRule) => void
}) {
  return (
    <div className="rounded-xl ring-1 ring-border bg-card/40 overflow-hidden">
      <div className="px-4 py-2.5 bg-muted/40 border-b border-border flex items-center gap-2.5">
        {section.icon}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground">
            {section.title} <span className="text-xs font-normal text-muted-foreground">({rules.length})</span>
          </div>
          <div className="text-[11px] text-muted-foreground">{section.description}</div>
        </div>
        <button
          onClick={onStartCreate}
          disabled={creating}
          className="shrink-0 px-3 py-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded hover:bg-primary/90 transition disabled:opacity-50 flex items-center gap-1"
        >
          <Plus size={12} /> Add rule
        </button>
      </div>

      {creating && (
        <div className="p-4 border-b border-border bg-muted/20">
          <RuleForm serverId={serverId} departmentId={deptId} type={section.type} onSaved={onSaved} onCancel={onCancelForm} />
        </div>
      )}

      {rules.length === 0 && !creating ? (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">No {section.title.toLowerCase()} yet.</div>
      ) : (
        <div className="divide-y divide-border">
          {rules.map((r) =>
            editingRuleId === r.id ? (
              <div key={r.id} className="p-4 bg-muted/20">
                <RuleForm serverId={serverId} departmentId={deptId} type={section.type} initial={r} onSaved={onSaved} onCancel={onCancelForm} />
              </div>
            ) : (
              <RuleRow
                key={r.id}
                rule={r}
                onEdit={() => onStartEdit(r)}
                onToggleEnabled={() => onToggleEnabled(r)}
                onDelete={() => onDelete(r)}
              />
            ),
          )}
        </div>
      )}
    </div>
  )
}

function RuleRow({ rule, onEdit, onToggleEnabled, onDelete }: {
  rule: GovernanceRule
  onEdit: () => void
  onToggleEnabled: () => void
  onDelete: () => void
}) {
  const recipientCount = rule.recipientIds?.length ?? 0
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-accent/20 transition">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${rule.enabled ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{rule.name}</span>
          <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${actionChipClass(rule.action)}`}>
            {rule.action}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          {rule.surfaces.map((s) => (
            <span key={s} className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{s}</span>
          ))}
          {rule.type === 'barrier' && rule.barrierA && rule.barrierB && (
            <span className="text-[11px] text-muted-foreground font-mono">
              {rule.barrierA.kind}:{rule.barrierA.id.slice(0, 8)} ⇄ {rule.barrierB.kind}:{rule.barrierB.id.slice(0, 8)}
            </span>
          )}
          <span className="text-[11px] text-muted-foreground">
            {recipientCount > 0 ? `${recipientCount} recipient${recipientCount === 1 ? '' : 's'}` : 'Default recipients (dept head / server admins)'}
          </span>
        </div>
      </div>
      <Toggle checked={rule.enabled} onChange={onToggleEnabled} />
      <button onClick={onEdit} className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent/40 transition" title="Edit">
        <Pencil size={14} />
      </button>
      <button onClick={onDelete} className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition" title="Delete">
        <Trash2 size={14} />
      </button>
    </div>
  )
}

function Toggle({ checked, onChange, disabled, label }: { checked: boolean; onChange: () => void; disabled?: boolean; label?: string }) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      {label && <span className="text-xs font-medium text-muted-foreground">{label}</span>}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={onChange}
        className={`h-5 w-9 rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed ${checked ? 'bg-primary' : 'bg-muted'}`}
      >
        <span className={`block h-4 w-4 rounded-full bg-white transition ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
    </div>
  )
}
